<template>
  <div>
    <div class="d-flex justify-content-between" :class="IS_MOBILE() ? 'flex-column' : 'align-items-center'">
      <component :is="IS_MOBILE() ? 'h5' : 'h3'">
        {{ $t('Change Time Limits for ') }} {{ currentProject }}
      </component>

      <b-form-group :class="IS_MOBILE() ? 'w-100' : 'w-50'">
        <b-form-input v-model="keyword" :placeholder="$t('Search Users')" @input="getUsers(1)" />
      </b-form-group>
    </div>

    <b-overlay :show="isProcessing">
      <b-table :fields="fields" :items="users" responsive show-empty :busy="isProcessing"
        @row-hovered="(_, index) => hoveredRowIndex = index" @row-unhovered="() => hoveredRowIndex = null"
        @row-clicked="item => IS_MOBILE() ? startEditing(item) : ''">
        <template #head(name)="row">
          <div class="d-flex">
            <span>{{ row.label }}</span>
            <table-header-sort :sort-label="'NAME'" :sort-options="{ sortBy: sortBy, sortDirection: sortDirection }"
              @update:sort-options="options => { sortBy = options.sortBy; sortDirection = options.sortDirection; getUsers(1); }" />
          </div>
        </template>
        <template #cell(name)="row">
          <div :style="IS_MOBILE() ? 'width: 50vw;' : ''">
            <user-with-tags :user="row.item" />
          </div>
        </template>

        <template #cell(hours)="row">
          <!-- View Mode -->
          <div v-if="!row.item.isEditing" class="d-inline-flex align-items-center time-limit-display">
            <div :class="{ 'editable-text': hoveredRowIndex === row.index }" @click="startEditing(row.item)">
              <span v-if="row.item.hours">
                {{ row.item.hours }} hrs / {{ row.item.limitType === 'WEEKLY' ? 'Week' : 'Month' }}
              </span>
              <span v-else class="text-muted">No Limit</span>
            </div>
            <feather-icon v-if="hoveredRowIndex === row.index" icon="EditIcon" size="16"
              class="ml-2 cursor-pointer text-primary" @click="startEditing(row.item)" />
          </div>

          <!-- Edit Mode -->
          <div v-else :class="IS_MOBILE() ? '' : 'd-flex align-items-center flex-nowrap'">
            <div class="d-flex" :class="{ 'mb-1': IS_MOBILE() }">
              <validation-observer ref="limitForm">
                <validation-provider name="limit hour" rules="min_value:1" #default="{ errors }">
                  <b-form-input v-model="row.item.editHours" type="number" size="sm" placeholder="Hours" class="mr-1"
                    autocomplete="off" style="width: 70px;" :min="1" @keyup.esc="cancelEdit(row.item)"
                    @keyup.enter="saveEdit(row.item)" />
                  <span v-if="errors[0]" class="text-danger small">{{ errors[0] }}</span>
                </validation-provider>
              </validation-observer>

              <v-select v-model="row.item.editType" :reduce="limit => limit.value" :clearable="false"
                :options="limitForOptions" :append-to-body="true" :calculate-position="withPopper"
                class="mr-1 limit-select" style="width: 95px; min-width: 95px;"
                @option:selected="(option) => { row.item.editType = option.value; }" />
            </div>

            <div class="d-flex">
              <b-button variant="success" size="sm" class="mr-1 p-1 btn-icon-sm"
                v-b-tooltip.hover="'Confirm'"
                :disabled="isProcessing || !row.item.editHours || row.item.editHours <= 0" @click="saveEdit(row.item)">
                <feather-icon icon="CheckIcon" size="14" />
              </b-button>

              <b-button
                v-b-tooltip.hover="'Cancel'"
                variant="secondary" size="sm" class="mr-1 p-1 btn-icon-sm" @click="cancelEdit(row.item)">
                <feather-icon icon="XIcon" size="14" />
              </b-button>

              <b-button 
                v-if="row.item.hours" 
                v-b-tooltip.hover="'Reset Time Limit'"
                variant="outline-danger" size="sm" class="p-1 btn-icon-sm"
                :disabled="isProcessing" @click="confirmDelete(row.item)">
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </div>
          </div>
        </template>
      </b-table>
    </b-overlay>

    <div v-if="total > perPage" class="w-100 text-center">
      <b-pagination v-model="currentPage" :total-rows="total" :per-page="perPage" class="my-2" @input="getUsers" />
    </div>

    <!-- Time Limit Delete Modal -->
    <b-modal id="time-limit-delete-modal"
      :title="selectedUser ? `Remove Time Limit for ${selectedUser.name}` : 'Remove Time Limit'" ok-title="Remove"
      ok-variant="danger" @ok="deleteTimeLimit">
      <span v-if="selectedUser">Are you sure you want to remove time limit for {{ selectedUser.name }}?</span>
    </b-modal>

    <confirm-modal v-if="selectedUser" modal="confirm-change-modal" title="Discard Changes?"
      :description="`All the changes made to ${selectedUser.name}'s time limit will be lost. Do you want to proceed?`"
      ok-title="Confirm" ok-variant="danger" @confirm="onConfirm" />
  </div>
</template>

<script setup>
import useApollo from '@/plugins/graphql/useApollo';
import store from '@/store';
import { ref, onMounted, getCurrentInstance } from 'vue';
import UserWithTags from '@/views/common/components/UserWithTags.vue';
import vSelect from 'vue-select';
import { BModal, BTable, BOverlay, BFormInput, BFormGroup, BButton, BPagination, VBTooltip } from 'bootstrap-vue';
import _ from 'lodash';
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue';
import { createPopper } from '@popperjs/core';

const root = getCurrentInstance().proxy.$root;
const keyword = ref('');
const total = ref(0);
const perPage = ref(10);
const currentPage = ref(1);
const users = ref([]);
const isProcessing = ref(false);
const selectedUser = ref(null);
const tempUser = ref(null);
const hoveredRowIndex = ref(null);
const sortBy = ref('NAME');
const sortDirection = ref('ASC');
const currentProject = store.state.project.project.name;

const withPopper = (dropdownList, component, { width }) => {
  dropdownList.style.width = width;
  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, -1] },
      },
      {
        name: 'toggleClass',
        enabled: true,
        phase: 'write',
        fn({ state }) {
          component.$el.classList.toggle('drop-up', state.placement === 'top');
        },
      },
    ],
  });
  return () => popper.destroy();
};

defineExpose({
  directives: {
    'b-tooltip': VBTooltip,
  }
})

const fields = [
  { label: 'User', key: 'name', thClass: 'text-capitalize w-50' },
  { label: 'Time Limit', key: 'hours', thClass: 'text-capitalize w-50' },
]

const limitForOptions = [
  {
    value: 'WEEKLY',
    label: 'Weekly',
  },
  {
    value: 'MONTHLY',
    label: 'Monthly',
  },
]

const limitForm = ref(null);

const onConfirm = () => {
  cancelEdit(selectedUser.value)
  root.$nextTick(() => {
    startEditing(tempUser.value)
  })
}

const startEditing = (item) => {
  if (selectedUser.value) {
    if (selectedUser.value.editHours !== selectedUser.value.hours || selectedUser.value.editType !== selectedUser.value.limitType) {
      if ((selectedUser.value.hours && selectedUser.value.limitType) || selectedUser.value.editHours) {
        tempUser.value = item;
        root.$bvModal.show('confirm-change-modal');
        return;
      }
    }

    cancelEdit(selectedUser.value);
  }

  selectedUser.value = users.value.find(user => user.uuid === item.uuid);
  selectedUser.value.isEditing = true;
  selectedUser.value.editHours = item.hours || '';
  selectedUser.value.editType = item.limitType || 'WEEKLY';
  tempUser.value = null;
}

const cancelEdit = (item) => {
  const user = users.value.find(u => u.uuid === item.uuid)
  user.isEditing = false;
  user.editHours = null;
  user.editType = null;
  selectedUser.value = null;
}

const confirmDelete = (item) => {
  selectedUser.value = item;
  root.$bvModal.show('time-limit-delete-modal');
}

const saveEdit = (item) => {
  if (!item.editHours || item.editHours <= 0) {
    root.showErrorMessage({ data: { message: 'Please enter a valid number of hours (minimum 1)' } });
    return;
  }

  isProcessing.value = true;
  useApollo.team.updateTeamMemberLimit({
    userUid: item.uuid,
    projectUid: store.state.project.selectedProject,
    hours: parseInt(item.editHours, 10),
    type: item.editType,
  }).then(res => {
    item.hours = parseInt(item.editHours, 10);
    item.limitType = item.editType;
    item.isEditing = false;
    selectedUser.value = null;
    root.showSuccess(res.data.updateUserProjectTimeLimit.message);
  }).catch(error => {
    root.showErrorMessage(error);
  }).finally(() => {
    isProcessing.value = false;
  });
}

const getUsers = _.debounce((page = null) => {
  isProcessing.value = true;
  if (page) currentPage.value = page;
  useApollo.project.getAllUsersTimeLimit({
    projectUid: store.state.project.selectedProject,
    keyword: keyword.value,
    page: currentPage.value,
    first: perPage.value,
    sortBy: sortBy.value,
    sortDirection: sortDirection.value,
  }).then(response => {
    users.value = response.data.users.data.map(({ projectSettings, ...user }) => ({
      ...user,
      hours: projectSettings?.data?.[0]?.information.hours,
      limitType: projectSettings?.data?.[0]?.information.type,
      isEditing: false,
      editHours: null,
      editType: null,
    }));
    total.value = response.data.users.total;
  }).finally(() => {
    isProcessing.value = false;
  });
}, 500)

const deleteTimeLimit = () => {
  isProcessing.value = true
  useApollo.team.updateTeamMemberLimit({
    userUid: selectedUser.value.uuid,
    projectUid: store.state.project.selectedProject,
    hours: 0,
    type: selectedUser.value.limitType ?? 'WEEKLY',
  }).then(res => {
    const user = users.value.find(u => u.uuid === selectedUser.value.uuid)
    user.hours = null
    user.limitType = null
    user.isEditing = false
    limitForm.value.reset()
    root.showSuccess(res.data.updateUserProjectTimeLimit.message)
  }).catch(error => {
    root.showErrorMessage(error)
  }).finally(() => {
    isProcessing.value = false
    selectedUser.value = null
  })
}

onMounted(() => {
  getUsers();
})
</script>

<style scoped>
.icon-btn {
  padding: 10px;
}

.time-limit-display {
  min-height: 38px;
}

.editable-text {
  border-bottom: 1px dashed #7367f0;
  cursor: pointer;
  padding-bottom: 2px;
}

.btn-icon-sm {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.limit-select>>>.vs__dropdown-toggle {
  min-height: 28px;
  height: 28px;
  padding: 0 0 0 8px;
}

.limit-select>>>.vs__selected {
  font-size: 13px;
  margin: 2px 2px 2px 0;
  padding: 0 2px;
}

.limit-select>>>.vs__actions {
  padding: 2px 4px 0 0;
}

.limit-select>>>.vs__search {
  margin: 0;
  padding: 0;
}
</style>
